// Generic imports
import React, { useRef, useState } from 'react';
import _ from 'lodash';
import { useDrag } from 'react-dnd'
import pluralize from 'pluralize';

// Font Awesome import
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faGripDotsVertical } from '@fortawesome/pro-solid-svg-icons';
import { faArrowsRotate } from '@fortawesome/pro-light-svg-icons';

// Material UI Imports
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Switch from '@mui/material/Switch';
import { grey } from '@mui/material/colors';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import Link from '@mui/material/Link';
import CircularProgress from '@mui/material/CircularProgress';

// Local Imports
import currencyToText from './CurrencyToText';
import { getMultiplier } from './CalcWeight.js';
import { formulaToReact } from './utils/Formula.js';
import { FieldTypes } from './FieldTypes.js';

// Monada Library Imports
import { 
    DetailsCard as MonadaOkargoDetailsCard,
} from '@monada-ai/monada-okargo-integration/dist/client';
import { 
    DetailsCard as MonadaCargoAiDetailsCard,
} from '@monada-ai/monada-cargoai-integration/dist/client';
import { 
    DetailsCard as MonadaCargoFiveDetailsCard,
} from '@monada-ai/monada-cargofive-integration/dist/client';

function TruncatedText({ text, maxLength = 30 }) {
    const [expanded, setExpanded] = useState(false);
    // A Text cost happily holds digits, and those are stored as a number rather than a
    // string. A number has no .length, so `text.length <= maxLength` was false and the
    // code fell through to .slice(), which a number does not have - throwing during
    // render and taking the whole rate search down with it. Render whatever it is as text.
    const value = text === null || text === undefined ? '' : String(text);
    if (!value || value.length <= maxLength) return value;
    if (expanded) return <>{value} <Link component='button' variant='caption' onClick={() => setExpanded(false)} sx={{ verticalAlign: 'baseline' }}>show less</Link></>;
    return <>{value.slice(0, maxLength)}... <Link component='button' variant='caption' onClick={() => setExpanded(true)} sx={{ verticalAlign: 'baseline' }}>view more</Link></>;
}

function CollapsibleSource({ content, theme, onReparse, reparsing }) {
    const [expanded, setExpanded] = useState(false);

    const formatted = (() => {
        if (!content) return null;
        const lines = content.split('\n');
        const isEmail = lines[0]?.startsWith('Subject:');
        if (isEmail) {
            const subject = lines[0].replace('Subject:', '').trim();
            const body = lines.slice(1).join('\n').trim();
            const isHtml = body.includes('<html') || body.includes('<div') || body.includes('<br');
            return { subject, body, isHtml };
        }
        const isHtml = content.includes('<html') || content.includes('<div') || content.includes('<br');
        return { subject: null, body: content.trim(), isHtml };
    })();

    return (
        <Box sx={{ padding: '0px 12px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Link component='button' variant='caption' sx={{ fontWeight: 800 }} onClick={() => setExpanded(e => !e)}>
                    {expanded ? 'Hide source content' : 'View source content'}
                </Link>
                {onReparse && (
                    <Link component='button' variant='caption' disabled={reparsing} sx={{ fontWeight: 800, display: 'inline-flex', alignItems: 'center', gap: '5px' }} onClick={() => onReparse()}>
                        {reparsing ? <CircularProgress size={11} /> : <FontAwesomeIcon icon={faArrowsRotate} style={{ fontSize: '11px' }} />}
                        Re-parse with AI
                    </Link>
                )}
            </Box>
            {expanded && formatted && (
                <Box sx={{ padding: '12px 16px', fontSize: '13px', marginTop: '6px', border: `1px solid ${theme.palette.border1}`, background: '#fafbfc', borderRadius: '6px', maxHeight: '400px', overflowY: 'auto' }}>
                    {formatted.subject && (
                        <Box sx={{ mb: '8px', pb: '8px', borderBottom: '1px solid #e2e8f0' }}>
                            <Typography variant='caption' sx={{ color: '#64748b', fontWeight: 600, fontSize: '11px' }}>Subject</Typography>
                            <Typography variant='body2' sx={{ fontWeight: 600, fontSize: '13px' }}>{formatted.subject}</Typography>
                        </Box>
                    )}
                    {formatted.isHtml ? (
                        <Box
                            sx={{ fontSize: '13px', lineHeight: 1.6, color: '#334155', wordBreak: 'break-word', '& blockquote': { borderLeft: '3px solid #cbd5e1', pl: '12px', ml: '4px', color: '#64748b' }, '& pre': { whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 'inherit' }, '& table': { fontSize: 'inherit' }, '& img': { maxWidth: '100%' } }}
                            dangerouslySetInnerHTML={{ __html: formatted.body }}
                        />
                    ) : (
                        <Box sx={{ fontSize: '12px', lineHeight: 1.7, color: '#334155', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit' }}>
                            {formatted.body}
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
}

function RatesDetails(props) {
    const { organization, rates, emphasize, setEmphasize, onStartDragging, onEndDragging, allowEditing, transportationMethod, opportunityProducts, onReparseSource, reparsingSource } = props;

    if ((rates.length === 1) && (!!_.get(rates[0], 'attributes.okargoOffer.chargeSet', null))) return (
        <MonadaOkargoDetailsCard
            rate={rates[0]} 
            emphasize={emphasize} 
            setEmphasize={setEmphasize}
            onStartDragging={onStartDragging}
            onEndDragging={onEndDragging}
            allowEditing={allowEditing}
        />
    );

    if ((rates.length === 1) && (!!_.get(rates[0], 'attributes.cargoaiOffer', null))) {
        return (
            <MonadaCargoAiDetailsCard
                rate={rates[0]} 
                emphasize={emphasize} 
                setEmphasize={setEmphasize}
                onStartDragging={onStartDragging}
                onEndDragging={onEndDragging}
                FieldTypes={FieldTypes}
                currencyToText={currencyToText}
                organization={organization}
            />
        );
    }

    if ((rates.length === 1) && (!!_.get(rates[0], 'attributes.cargofiveRate', null))) {
        return (
            <MonadaCargoFiveDetailsCard
                rate={rates[0]}
                emphasize={emphasize}
                setEmphasize={setEmphasize}
                onStartDragging={onStartDragging}
                onEndDragging={onEndDragging}
            />
        );
    }

    return (
        <MonadaDetails
            organization={organization}
            rate={rates[0]}
            transportationMethod={transportationMethod}
            emphasize={emphasize}
            setEmphasize={setEmphasize}
            onStartDragging={onStartDragging}
            onEndDragging={onEndDragging}
            opportunityProducts={opportunityProducts}
            onReparseSource={onReparseSource}
            reparsingSource={reparsingSource}
        />
    )
}

function MonadaSectionDetails(props) {
    const { organization, section, rate, onStartDragging, onEndDragging, emphasize, setEmphasize, transportationMethod, originalSection, calcProducts } = props;

    const _rate = useRef(rate);
    _rate.current = rate;

    const [, drag] = useDrag(() => ({
        type: 'RateCardSingleSection',
        item: () => {
            onStartDragging();
            // Strip the client-only `_rfqSource` display marker so it never enters
            // opportunity.buyingRates — the server's strict schema rejects unknown keys on save.
            return { rate: _.omit(_rate.current, '_rfqSource'), section };
        },
        collect: (monitor) => ({
            isDragging: !!monitor.isDragging()
        }),
        end: () => {
            onEndDragging();
        }
    }))

    let showSection = false;

    _.forEach((section.offers[0] || {}).fields, field => {
        const fieldType = (FieldTypes[rate.transportationMethod || transportationMethod] || []).find(f => f.value === field.type);
        const chargeLine = fieldType?.getChargeLines ? fieldType.getChargeLines({ field, products: calcProducts || [rate.product], organization }) : null;

        if (!chargeLine) return;

        const fieldValueId = chargeLine[0].fieldValueId;
        const value = _.get(field, `values[${fieldValueId}].value`, null);
        const formula = _.get(field, `values[${fieldValueId}].formula`, null);
        if (((value !== null) && (!_.isNaN(parseFloat(value)))) || (formula)) showSection = true;
    });

    if (!showSection) return;

    return (
        <Box key={section.title} sx={{ padding: '12px' }} ref={drag}>
            <Box sx={{ fontWeight: 800, fontSize: '15px', cursor: 'move', display: 'flex', alignItems: 'center' }}>
                <Box sx={{ fontSize: '18px', marginRight: '12px' }}>
                    <FontAwesomeIcon icon={faGripDotsVertical} />
                </Box>
                <Box>
                    {section.title}
                </Box>
            </Box>
            <Box sx={{ display: 'flex', marginTop: '12px', flexWrap: 'wrap' }}>
                {section.offers[0].fields.map(field => {
                    const originalField = originalSection?.offers?.[0]?.fields?.find(f => f.id === field.id);
                    return (
                        <MonadaSingleFieldDetails
                            key={field.id}
                            organization={organization}
                            rate={rate}
                            field={field}
                            originalField={originalField}
                            emphasize={!!(emphasize || []).find(e => e.rateId === rate.id && e.fieldId === field.id)}
                            setEmphasize={!setEmphasize ? null : emphasized => {
                                const newEmphasize = _.cloneDeep(emphasize || []);
                                if (emphasized) {
                                    newEmphasize.push({ rateId: rate.id, fieldId: field.id });
                                } else {
                                    newEmphasize.splice(newEmphasize.findIndex(e => e.rateId === rate.id && e.fieldId === field.id), 1);
                                }
                                setEmphasize(_.uniqWith(newEmphasize, _.isEqual));
                            }}
                            onStartDragging={onStartDragging}
                            onEndDragging={onEndDragging}
                            transportationMethod={transportationMethod}
                            calcProducts={calcProducts}
                        />
                    )
                })}
                {originalSection?.offers?.[0]?.fields
                    ?.filter(origField => !section.offers[0].fields.find(f => f.id === origField.id))
                    .map(deletedField => {
                        const fieldType = (FieldTypes[rate.transportationMethod || transportationMethod] || []).find(f => f.value === deletedField.type);
                        const chargeLine = fieldType?.getChargeLines ? fieldType.getChargeLines({ field: deletedField, products: calcProducts || [rate.product], organization }) : null;
                        if (!chargeLine) return null;
                        const { multiplierText, fieldValueId } = chargeLine[0];
                        const value = _.get(deletedField, `values[${fieldValueId}]`, null);
                        if (!value) return null;
                        const fraction = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
                        return (
                            <Box
                                key={`deleted-${deletedField.id}`}
                                sx={{
                                    padding: '10px',
                                    width: '100%',
                                    minWidth: '100%',
                                    marginBottom: '12px',
                                    border: '1px solid',
                                    borderColor: grey[300],
                                    opacity: 0.6,
                                }}
                            >
                                <Box sx={{ padding: '10px', display: 'flex', alignItems: 'center', width: '100%', minWidth: '100%' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <Box sx={{ fontSize: '12px', marginBottom: '6px', textDecoration: 'line-through', color: grey[500] }}>
                                            {!value.formula && currencyToText(value.currency, true, fraction.format(value.value))}
                                            {multiplierText ? ` / ${pluralize(multiplierText, 1)}` : ''}
                                        </Box>
                                        <Box sx={{ fontSize: '12px', color: grey[500], textDecoration: 'line-through' }}>
                                            {deletedField.title || 'No field name'}
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })
                }
            </Box>
        </Box>
    );
}

function MonadaSingleFieldDetails(props) {
    const { organization, rate, field, originalField, emphasize, setEmphasize, onStartDragging, onEndDragging, transportationMethod, calcProducts } = props;
    const theme = useTheme();

    const _rate = useRef(rate);
    _rate.current = rate;

    const [, drag] = useDrag(() => ({
        type: 'RateCardSingleRate',
        item: () => {
            onStartDragging();
            // Strip the client-only `_rfqSource` display marker so it never enters
            // opportunity.buyingRates — the server's strict schema rejects unknown keys on save.
            return { rate: _.omit(_rate.current, '_rfqSource'), field };
        },
        collect: (monitor) => ({
            isDragging: !!monitor.isDragging()
        }),
        end: () => {
            onEndDragging();
        }
    }))

    const fieldType = (FieldTypes[rate.transportationMethod || transportationMethod] || []).find(f => f.value === field.type);
    const chargeLine = fieldType?.getChargeLines ? fieldType.getChargeLines({ field, products: calcProducts || [rate.product], organization }) : null;
    if (!chargeLine) return null;

    const { multiplierText, fieldValueId } = chargeLine[0];
    const value = _.get(field, `values[${fieldValueId}]`, null);
    const originalValue = originalField ? _.get(originalField, `values[${fieldValueId}]`, null) : null;
    const hasValueChanged = originalValue && !originalValue.formula && !value?.formula
        && originalValue.value !== value?.value;
    const hasTitleChanged = originalField && originalField.title !== field.title;

    const fraction = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });

    return (
        <Box
            sx={{
                cursor: 'move',
                // The row inside already sets the gap to the border. Padding here too made it 20px
                // on every side, so a two-line charge sat in a box twice its height.
                width: '100%',
                minWidth: '100%',
                marginBottom: '12px',
                border: `1px solid ${theme.palette.border1}`,
                background: theme.palette.panel1,
            }}
        >
            <Box
                ref={drag}
                sx={{
                    background: theme.palette.panel1,
                    padding: '10px 12px',
                    cursor: 'move',
                    fontWeight: emphasize ? '800' : '400',
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    minWidth: '100%',
                }}
            >
                <Box sx={{ fontWeight: emphasize ? '800' : '400', fontSize: '18px' }}>
                    <FontAwesomeIcon icon={faGripDotsVertical} />
                </Box>
                <Box sx={{ padding: '0px 12px', color: grey[400] }}>
                |
                </Box>
                <Box sx={{ fontSize: '10px', fontWeight: emphasize ? '800' : '400', whiteSpace: 'nowrap' }}>
                    {rate.product.type}
                </Box>
                <Box sx={{ padding: '0px 12px', color: grey[400] }}>
                |
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <Box sx={{ fontSize: '12px', fontWeight: emphasize ? '800' : '400', marginBottom: '6px' }}>
                        {hasValueChanged && (
                            <span style={{ textDecoration: 'line-through', color: grey[500], marginRight: '8px' }}>
                                {currencyToText(originalValue.currency, true, fraction.format(originalValue.value))}
                            </span>
                        )}
                        {!value?.formula && value && (
                            <>
                                {currencyToText(value.currency, true, fraction.format(value.value))}
                            </>
                        )}
                        {value?.formula
                                ? formulaToReact({
                                    formula: value.formula,
                                    currency: value.currency,
                                    multiplierText,
                                    sections: rate.offer.sections,
                                    width: 'auto',
                                    errorValue: <Tooltip title={value.formula}><i style={{ borderBottom: '1px dotted' }}>Formula</i></Tooltip>
                                }) : null
                        }
                        {multiplierText ? ` / ${pluralize(multiplierText, 1)}` : ''}
                    </Box>
                    <Box sx={{ fontSize: '12px', color: grey[600] }}>
                        {hasTitleChanged && (
                            <span style={{ textDecoration: 'line-through', color: grey[400], marginRight: '8px' }}>
                                {originalField.title}
                            </span>
                        )}
                        {field.title || 'No field name'}
                    </Box>
                </Box>
                {setEmphasize && (
                    <Switch checked={emphasize} onChange={() => setEmphasize(!emphasize)} />
                )}
            </Box>
        </Box>
    )
}

function MonadaDetails(props) {
    const { rate, emphasize, setEmphasize, onStartDragging, onEndDragging, transportationMethod, organization, opportunityProducts, onReparseSource, reparsingSource } = props;
    const theme = useTheme();
    const originalRateStructure = rate.attributes?.originalRateStructure;

    // Build aggregated products for multi-product calculations (same pattern as RateCard)
    const calcProducts = React.useMemo(() => {
        if (!opportunityProducts) return [rate.product];
        // For LCL: use the opportunity product's calculations for correct W/M
        const lclProduct = opportunityProducts.find(p => p.type === 'LCL' && p.calculations);
        if (lclProduct && rate.product?.type === 'LCL') {
            return [{ ...rate.product, calculations: lclProduct.calculations, kgOrLbs: lclProduct.kgOrLbs, cmOrInches: lclProduct.cmOrInches, densityRatio: rate.product.densityRatio }];
        }
        // For Air freight (unified): use the opportunity product's calculations
        const afProduct = opportunityProducts.find(p => p.type === 'Air freight' && p.calculations);
        if (afProduct && rate.product?.type === 'Air freight') {
            return [{ ...rate.product, calculations: afProduct.calculations, kgOrLbs: afProduct.kgOrLbs, cmOrInches: afProduct.cmOrInches, roundChargeableWeight: afProduct.roundChargeableWeight }];
        }
        if (opportunityProducts.length <= 1) return [rate.product];
        const sameType = opportunityProducts.filter(p => p.type === rate.product.type);
        if (sameType.length <= 1) return [rate.product];
        const totalWeight = _.sumBy(sameType, p => (p.weight || 0) * (p.quantity || 1));
        const totalCW = _.sum(sameType.map(p => getMultiplier({ product: p, transportationMethod: rate.transportationMethod || transportationMethod, allProducts: sameType, round: organization.airRoundChargeableWeight }).multiplier));
        return [{ ...rate.product, weight: totalWeight, quantity: 1, chargeableWeight: totalCW, volume: undefined, length: undefined, width: undefined, height: undefined }];
    }, [rate, opportunityProducts, organization, transportationMethod]);

    return (
        <Box>
            {rate.offer.sections.map((section, sectionIndex) => (
                <MonadaSectionDetails
                    key={sectionIndex}
                    organization={organization}
                    section={section}
                    rate={rate}
                    onStartDragging={onStartDragging}
                    onEndDragging={onEndDragging}
                    emphasize={emphasize}
                    setEmphasize={setEmphasize}
                    transportationMethod={transportationMethod}
                    originalSection={originalRateStructure?.offer?.sections?.find(s => s.id === section.id)}
                    calcProducts={calcProducts}
                />
            ))}

            {rate.attributes?.sourceContent && (
                <CollapsibleSource
                    content={rate.attributes.sourceContent}
                    theme={theme}
                    onReparse={onReparseSource ? () => onReparseSource(rate) : undefined}
                    reparsing={reparsingSource}
                />
            )}

            <Box sx={{ whiteSpace: 'wrap' }}>
                {_.flatten(rate.offer.sections.map((section, sectionIndex) => {
                    return _.map((section.offers[0] || {}).fields, field => {
                        if (field.type === 'string' && field.values.string?.value) {
                            return <span key={field.id}><b>{field.title}:</b> <TruncatedText text={field.values.string.value} maxLength={30} />{` · `}</span>
                        }
                    })
                }))}
                {/* attributes.source doubles as a provider marker for some rates (e.g. 'metro-tariff'),
                    not a real file URL. Only offer the download when it's an actual downloadable URL —
                    a bare marker resolves as a relative href and navigates the SPA to a bogus route,
                    which trips the Trusted Types "requires TrustedHTML assignment" block. */}
                {/^(https?:|blob:|data:)/i.test(_.get(rate, 'attributes.source', '') || '') && <a href={rate.attributes.source} target={'_blank'} rel={'noreferrer'}>Download source file</a>}
            </Box>
        </Box>
    )
}

export default RatesDetails;
